/**
 * ArenaViewToggle — the "Mine | Open" segmented control at the top of the Arena tab.
 * "Mine" = your challenges (the existing list). "Open" = the public board of takeable
 * challenges. specs/open-challenges (placement decision: toggle, not a 5th tab).
 */

import React, { useMemo } from 'react';
import {
  View, Text, Pressable, Platform,
} from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useTheme } from '../../../src/theme/useTheme';
import { getArenaViewToggleStyles } from './ArenaViewToggle.styles';

export type ArenaViewMode = 'mine' | 'open';

interface ArenaViewToggleProps {
  value: ArenaViewMode;
  onChange: (mode: ArenaViewMode) => void;
}

// Labels are spelled out rather than "Mine | Open": testers could not tell that "Open" was the
// public board of challenges from strangers.
const OPTIONS: { key: ArenaViewMode; label: string }[] = [
  { key: 'mine', label: 'My Challenges' },
  { key: 'open', label: 'Open Board' },
];

export const ArenaViewToggle: React.FC<ArenaViewToggleProps> = ({ value, onChange }) => {
  const theme = useTheme();
  const styles = useMemo(() => getArenaViewToggleStyles(theme), [theme]);

  return (
    <View style={styles.row} testID="arena-view-toggle" accessibilityRole="tablist">
      {OPTIONS.map((opt) => {
        const active = value === opt.key;
        return (
          <Pressable
            key={opt.key}
            onPress={() => onChange(opt.key)}
            testID={`arena-view-toggle-${opt.key}`}
            accessibilityRole="tab"
            // react-native-web does not surface accessibilityState.selected as aria-selected on a
            // Pressable, so a screen reader could not tell which view was active. RN 0.71+ accepts
            // the ARIA prop directly on both platforms.
            aria-selected={active}
            accessibilityState={{ selected: active }}
            // Same order and shared transition constant as the Arena filter bar, rather than
            // rebuilding the transition object inline on every render.
            style={({ pressed }) => [
              styles.pill,
              active && styles.pillActive,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.pillPressed,
            ]}
          >
            <Text style={[styles.label, active && styles.labelActive]} numberOfLines={1}>
              {opt.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
};
